<template>
  <!-- The absolute-full class ensures this fills the router-view without scrolling the page -->
  <div class="absolute-full bg-white">
    <q-layout view="hHh Lpr lff" container class="fit">
      <!-- Top Toolbar -->
      <q-header class="bg-white text-primary" bordered>
        <q-toolbar>
          <q-btn flat dense icon="tune" label="Options" no-caps @click="drawerOpen = !drawerOpen" />
          <q-space />

          <!-- Moved the mode toggle to the header so it's always accessible! -->
          <q-btn-toggle
            v-model="analysisMode"
            no-caps
            unelevated
            toggle-color="primary"
            color="white"
            text-color="primary"
            class="shadow-1"
            style="border: 1px solid var(--q-primary)"
            :options="[
              { label: 'Pitching Grid', value: 'pitch' },
              { label: 'Fielding Grid', value: 'hit' },
            ]"
          />
        </q-toolbar>
      </q-header>

      <!-- Drawer for Reporting Options -->
      <q-drawer v-model="drawerOpen" show-if-above :width="300" bordered class="bg-grey-1">
        <!-- q-scroll-area allows the options to scroll internally if the list gets too long -->
        <q-scroll-area class="fit q-pa-md">
          <div class="text-subtitle2 text-weight-bold q-mb-sm text-grey-8">1. Select Team</div>
          <q-select
            v-model="selectedTeam"
            :options="teams"
            option-label="name"
            label="Team"
            outlined
            dense
            class="bg-white q-mb-md"
          />

          <div class="text-subtitle2 text-weight-bold q-mb-sm text-grey-8">2. Select Player(s)</div>

          <!-- Pitcher Select (Single, defaults to All) -->
          <q-select
            v-if="analysisMode === 'pitch'"
            v-model="selectedPitcher"
            :options="pitcherOptions"
            option-label="name"
            label="Pitcher"
            outlined
            dense
            class="bg-white"
            :disable="!selectedTeam"
          >
            <template v-slot:no-option>
              <q-item
                ><q-item-section class="text-grey">Select a team first</q-item-section></q-item
              >
            </template>
          </q-select>

          <!-- NEW: Plate Appearance Grid (Only visible for a single pitcher) -->
          <div v-if="analysisMode === 'pitch' && selectedPitcher && selectedPitcher.id !== -1" class="q-mt-md">
            <div class="row justify-between items-center q-mb-sm">
              <div class="text-subtitle2 text-weight-bold text-grey-8">3. Plate Appearances</div>
              <q-btn
                v-if="selectedPA && selectedPA.id !== 'all'"
                flat dense color="primary" label="Clear Filter" size="sm"
                @click="selectedPA = { id: 'all' }"
              />
            </div>

            <div class="row q-col-gutter-sm">
              <div class="col-6" v-for="pa in pitcherPAOptions" :key="pa.id">
                <q-card
                  flat bordered
                  class="cursor-pointer transition-all"
                  :class="selectedPA?.id === pa.id ? 'bg-blue-1 border-primary' : 'bg-white'"
                  style="border-width: 2px"
                  @click="selectedPA = (selectedPA?.id === pa.id) ? { id: 'all' } : pa"
                >
                  <q-card-section class="q-pa-xs text-center">
                    <div style="font-size: 0.65rem; font-weight: bold;" class="text-primary ellipsis">Inn {{ pa.inning }} | Spot {{ pa.lineupSpot }}</div>
                    <div style="font-size: 0.65rem;" class="ellipsis">{{ pa.batterName }}</div>

                    <!-- Mini Pitch Grid -->
                    <div class="q-my-xs flex flex-center">
                      <svg viewBox="0 0 100 100" width="100%" style="max-width: 70px; background-color: #f5f5f5; border-radius: 4px;">
                        <rect x="20" y="25" width="60" height="50" fill="white" stroke="#333" stroke-width="1.5" />
                        <line x1="20" y1="41.6" x2="80" y2="41.6" stroke="#999" stroke-width="0.5" />
                        <line x1="20" y1="58.3" x2="80" y2="58.3" stroke="#999" stroke-width="0.5" />
                        <line x1="40" y1="25" x2="40" y2="75" stroke="#999" stroke-width="0.5" />
                        <line x1="60" y1="25" x2="60" y2="75" stroke="#999" stroke-width="0.5" />
                        <circle
                          v-for="(p, idx) in pa.pitches"
                          :key="'mini-pitch-' + idx"
                          :cx="20 + (p.x * 60)"
                          :cy="25 + (p.y * 50)"
                          r="3.5"
                          :fill="p.color"
                          stroke="#2c3e50"
                          stroke-width="0.5"
                          opacity="0.85"
                        />
                      </svg>
                    </div>

                    <div style="font-size: 0.6rem; font-weight: bold" class="ellipsis text-grey-8">{{ pa.outcome }}</div>
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </div>

          <!-- Batter Select (Multiple, defaults to All) -->
          <q-select
            v-else
            v-model="selectedBatters"
            :options="teamPlayers"
            option-label="name"
            label="Batter(s)"
            multiple
            use-chips
            outlined
            dense
            class="bg-white"
            :disable="!selectedTeam"
          >
            <template v-slot:no-option>
              <q-item
                ><q-item-section class="text-grey">Select a team first</q-item-section></q-item
              >
            </template>
          </q-select>

          <!-- Legends -->
          <div v-if="analysisMode === 'pitch'" class="q-mt-xl text-caption">
            <div class="text-weight-bold q-mb-xs">Pitch Legend:</div>
            <div class="row items-center">
              <div class="legend-dot bg-positive q-mr-sm"></div>
              Ball
            </div>
            <div class="row items-center">
              <div class="legend-dot bg-negative q-mr-sm"></div>
              Strike
            </div>
            <div class="row items-center">
              <div class="legend-dot bg-info q-mr-sm"></div>
              In Play
            </div>
          </div>
          <div v-if="analysisMode === 'hit'" class="q-mt-xl text-caption">
            <div class="text-weight-bold q-mb-xs">Hit Legend:</div>
            <div class="row items-center">
              <div class="legend-dot bg-info q-mr-sm"></div>
              Hit (Safe)
            </div>
            <div class="row items-center">
              <div class="legend-dot bg-negative q-mr-sm"></div>
              Out
            </div>
          </div>
        </q-scroll-area>
      </q-drawer>

      <!-- Main Canvas Area -->
      <q-page-container class="fit">
        <q-page class="column fit bg-grey-2 relative-position overflow-hidden">
          <!-- Empty States -->
          <div v-if="!selectedTeam" class="empty-state">
            <q-icon name="group" size="3rem" color="grey-4" />
            <div class="text-h6 text-grey-5 q-mt-md">Select a team to begin analysis</div>
          </div>
          <div v-else-if="analysisMode === 'pitch' && !selectedPitcher" class="empty-state">
            <q-icon name="sports_baseball" size="3rem" color="grey-4" />
            <div class="text-h6 text-grey-5 q-mt-md">
              Select a pitcher to view their strike zone
            </div>
          </div>
          <div
            v-else-if="analysisMode === 'hit' && selectedBatters.length === 0"
            class="empty-state"
          >
            <q-icon name="stadium" size="3rem" color="grey-4" />
            <div class="text-h6 text-grey-5 q-mt-md">
              Select batter(s) to view their spray chart
            </div>
          </div>
          <div v-else-if="dataCount === 0" class="empty-state">
            <q-icon name="search_off" size="3rem" color="grey-4" />
            <div class="text-h6 text-grey-5 q-mt-md">
              No chartable data found for this selection
            </div>
          </div>

          <!-- The Canvas Grid (Now isolated to fill exactly 100% of the page container) -->
          <div
            v-show="dataCount > 0"
            ref="canvasContainer"
            class="col-grow relative-position overflow-hidden"
          >
            <!-- Same 'absolute-top-left' fix we used in DemoFielding to stop resize loops! -->
            <v-stage class="absolute-top-left" v-if="stageSize.width > 0" :config="stageConfig">
              <v-layer>
                <!-- PITCHING GRID VIEW -->
                <template v-if="analysisMode === 'pitch'">
                  <v-rect :config="strikeZoneConfig" />
                  <v-line :config="gridLineH1" />
                  <v-line :config="gridLineH2" />
                  <v-line :config="gridLineV1" />
                  <v-line :config="gridLineV2" />

                  <!-- Plotted Pitches -->
                  <v-group v-for="pitch in plottedPitches" :key="pitch.id">
                    <v-circle :config="pitch" />
                    <v-text
                      :config="{
                        x: pitch.x + 10,
                        y: pitch.y - 10,
                        text: pitch.caption,
                        fontSize: 10,
                      }"
                    />
                  </v-group>
                </template>

                <!-- HITTING GRID VIEW -->
                <template v-if="analysisMode === 'hit'">
                  <v-rect :config="backgroundConfig" />
                  <v-wedge :config="outfieldWedgeConfig" />
                  <v-wedge :config="infieldDirtConfig" />
                  <v-regular-polygon :config="diamondConfig" />
                  <v-regular-polygon :config="homePlateConfig" />

                  <!-- RETROSHEET LAYOUT MODIFIERS -->
                  <v-line :config="retroLineLCConfig" />
                  <v-line :config="retroLineRCConfig" />
                  <v-arc :config="retroShortArcConfig" />

                  <!-- Plotted Hit Lines -->
                  <v-line
                    v-for="hit in plottedHits"
                    :key="'line-' + hit.id"
                    :config="{
                      points: [homeAnchorX, homeAnchorY, hit.x, hit.y],
                      stroke: hit.type === 'out' ? '#e74c3c' : '#2980b9',
                      strokeWidth: 2,
                      dash: getTrajectoryDash(hit.trajectory),
                    }"
                  />
                  <!-- Plotted Hits -->
                  <v-group v-for="hit in plottedHits" :key="hit.id">
                    <v-circle :config="hit" />
                    <v-text
                      :config="{
                        x: hit.x + 10,
                        y: hit.y - 10,
                        text: hit.caption,
                        fontSize: 10,
                      }"
                    />
                  </v-group>
                </template>
              </v-layer>
            </v-stage>
          </div>
        </q-page>
      </q-page-container>
    </q-layout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useFieldLayout } from 'src/helpers/helpers'
import { useGameStore } from 'stores/game-store'
import type { Team, BaseballPlayer, PitchTransaction, PlayTransaction } from 'src/stores/base-types'

const gameStore = useGameStore()

// -- SELECTION STATE --
const drawerOpen = ref(true)
const selectedTeam = ref<Team | null>(null)
const analysisMode = ref<'pitch' | 'hit'>('pitch')
const selectedPitcher = ref<BaseballPlayer | null>(null)
const selectedBatters = ref<BaseballPlayer[]>([])
// NEW: Store the selected Plate Appearance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectedPA = ref<any>({ id: 'all' })

// --- NEW: DYNAMIC TEAMS & PLAYERS ---
// Only allow selection of the teams currently playing!
const teams = computed<Team[]>(() => {
  const activeTeams: Team[] = []
  if (gameStore.teamVisiting) activeTeams.push(gameStore.teamVisiting)
  if (gameStore.teamHome) activeTeams.push(gameStore.teamHome)
  return activeTeams
})

// Pull players directly from the active game lineups (including substitutions!)
const teamPlayers = computed<BaseballPlayer[]>(() => {
  if (!selectedTeam.value) return []

  const isHome = selectedTeam.value.id === gameStore.teamHome?.id
  const lineup = isHome ? gameStore.homeLineup : gameStore.visitingLineup

  if (!lineup || lineup.length === 0) {
    return selectedTeam.value.players || []
  }

  const playersMap = new Map<number, BaseballPlayer>()

  lineup.forEach(entry => {
    if (entry.player && entry.player.id) {
      playersMap.set(entry.player.id, entry.player)
    }
    if (entry.substitutions) {
      entry.substitutions.forEach(sub => {
        if (sub.player && sub.player.id) {
          playersMap.set(sub.player.id, sub.player)
        }
      })
    }
  })

  return Array.from(playersMap.values())
})

const pitcherOptions = computed(() => {
  // eslint-disable-next-line
  return [{ id: -1, name: 'All Pitchers' } as BaseballPlayer, ...teamPlayers.value]
})

// Reset the PA filter whenever the pitcher changes
watch(selectedPitcher, () => {
  selectedPA.value = { id: 'all', label: 'All Plate Appearances' }
})

watch(selectedTeam, () => {
  if (selectedTeam.value) {
    selectedPitcher.value = pitcherOptions.value[0] || null
    selectedBatters.value = [...teamPlayers.value]
  } else {
    selectedPitcher.value = null
    selectedBatters.value = []
  }
})

// -- CANVAS RESPONSIVENESS --
const canvasContainer = ref<HTMLElement | null>(null)
const stageSize = ref({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (canvasContainer.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        stageSize.value = { width: entry.contentRect.width, height: entry.contentRect.height }
      }
    })
    resizeObserver.observe(canvasContainer.value)
  }
})

onUnmounted(() => {
  if (resizeObserver && canvasContainer.value) resizeObserver.unobserve(canvasContainer.value)
})

const stageConfig = computed(() => ({
  width: stageSize.value.width,
  height: stageSize.value.height,
}))

const {
  homeAnchorX,
  homeAnchorY,
  fieldScale,
  backgroundConfig,
  outfieldWedgeConfig,
  infieldDirtConfig,
  diamondConfig,
  homePlateConfig,
  retroLineLCConfig,
  retroLineRCConfig,
  retroShortArcConfig,
  getTrajectoryDash,
} = useFieldLayout(stageSize)

// -- DATA FETCHING (IN-MEMORY) --
const rawPitches = ref<PitchTransaction[]>([])
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rawHits = ref<any[]>([])

const dataCount = computed(() => {
  if (analysisMode.value === 'pitch') return rawPitches.value.length
  return rawHits.value.length
})

// --- NEW: SYNCHRONOUS PINIA QUERIES ---
watch(
  // ADD selectedPA to the dependency array!
  [selectedPitcher, analysisMode, selectedTeam, selectedPA, () => gameStore.transactions.length],
  () => {
    if (analysisMode.value === 'pitch' && selectedPitcher.value) {
      if (selectedPitcher.value.id === -1) {
        // ALL PITCHERS
        const pIds = teamPlayers.value.map((p) => p.id)
        rawPitches.value = gameStore.transactions.filter(
          (t) => t.eventType === 'PITCH' && t.pitcherId !== null && pIds.includes(t.pitcherId)
        ) as PitchTransaction[]
      } else {
        // SINGLE PITCHER
        const id = selectedPitcher.value.id
        let pitches = gameStore.transactions.filter(
          (t) => t.eventType === 'PITCH' && t.pitcherId === id
        ) as PitchTransaction[]

        // NEW: Filter down to the specific PA if one is selected
        if (selectedPA.value && selectedPA.value.id !== 'all') {
          pitches = pitches.filter(p => p.plateAppearanceId === selectedPA.value.id)
        }

        rawPitches.value = pitches
      }
    } else {
      rawPitches.value = []
    }
  },
  { deep: true, immediate: true }
)

watch(
  [selectedBatters, analysisMode, () => gameStore.transactions.length],
  () => {
    if (analysisMode.value === 'hit' && selectedBatters.value.length > 0) {
      const ids = selectedBatters.value.map((b) => b.id)

      const hitPitches = gameStore.transactions.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (t: any) =>
          t.eventType === 'PITCH' &&
          t.batterId !== null &&
          ids.includes(t.batterId) &&
          t.hitLocationX != null
      ) as PitchTransaction[]

      const pitchIds = hitPitches.map((p) => String(p.id))

      const linkedPlays = gameStore.transactions.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (t: any) => t.eventType === 'PLAY' && pitchIds.includes(String(t.linkedPitchId))
      ) as PlayTransaction[]

      rawHits.value = hitPitches.map((pitch) => {
        const play = linkedPlays.find((pl) => String(pl.linkedPitchId) === String(pitch.id))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const actualOutcome = play?.outcome || (pitch as any).outcome || pitch.pitchOutcome || 'X'

        return {
          ...pitch,
          resolvedOutcome: actualOutcome,
        }
      })
    } else {
      rawHits.value = []
    }
  },
  { deep: true, immediate: true }
)

// -- PITCHING GEOMETRY & MAPPING --
const strikeZoneConfig = computed(() => {
  const w = stageSize.value.width * 0.8
  const h = stageSize.value.height * 0.65
  return {
    x: stageSize.value.width * 0.1,
    y: stageSize.value.height * 0.175,
    width: w,
    height: h,
    stroke: '#333',
    strokeWidth: 3,
  }
})

const gridLineH1 = computed(() => ({
  points: [
    strikeZoneConfig.value.x,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height / 3,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height / 3,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))
const gridLineH2 = computed(() => ({
  points: [
    strikeZoneConfig.value.x,
    strikeZoneConfig.value.y + (strikeZoneConfig.value.height / 3) * 2,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width,
    strikeZoneConfig.value.y + (strikeZoneConfig.value.height / 3) * 2,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))
const gridLineV1 = computed(() => ({
  points: [
    strikeZoneConfig.value.x + strikeZoneConfig.value.width / 3,
    strikeZoneConfig.value.y,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width / 3,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))
const gridLineV2 = computed(() => ({
  points: [
    strikeZoneConfig.value.x + (strikeZoneConfig.value.width / 3) * 2,
    strikeZoneConfig.value.y,
    strikeZoneConfig.value.x + (strikeZoneConfig.value.width / 3) * 2,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))

const plottedPitches = computed(() => {
  // 1. Grab the current dimensions of the dynamically drawn strike zone
  const sz = strikeZoneConfig.value

  return rawPitches.value.map((p) => {
    let color = '#7f8c8d'
    if (['B', 'W', 'H'].includes(p.pitchOutcome)) color = '#27ae60'
    else if (['C', 'S', 'F', 'T'].includes(p.pitchOutcome)) color = '#e74c3c'
    else if (p.pitchOutcome === 'X') color = '#2980b9'

    return {
      id: p.id,
      // 2. Multiply by the strike zone size, then add the strike zone's starting offset
      x: sz.x + (p.pitchX * sz.width),
      y: sz.y + (p.pitchY * sz.height),
      radius: 8,
      fill: color,
      stroke: '#2c3e50',
      strokeWidth: 1.5,
      caption: p.pitchOutcome,
      opacity: 0.8,
    }
  })
})

const plottedHits = computed(() => {
  return rawHits.value.map((h) => {
    const isHit = ['S', 'D', 'T', 'HR', '1B', '2B', '3B'].includes(h.resolvedOutcome)
    const color = isHit ? '#2980b9' : '#e74c3c'
    const type = isHit ? 'hit' : 'out'
    const trajStr = h.battedBallTrajectory ? `.${h.battedBallTrajectory}` : ''

    return {
      id: h.id,
      x: homeAnchorX.value + (h.hitLocationX || 0) * fieldScale.value,
      y: homeAnchorY.value + (h.hitLocationY || 0) * fieldScale.value,
      radius: 8,
      fill: color,
      stroke: '#2c3e50',
      strokeWidth: 1.5,
      opacity: 0.8,
      trajectory: h.battedBallTrajectory || 'G',
      caption: `${h.resolvedOutcome}${trajStr}`,
      type: type,
    }
  })
})

// --- UPDATED: PLATE APPEARANCE OPTIONS LOGIC ---
const pitcherPAOptions = computed(() => {
  if (!selectedPitcher.value || selectedPitcher.value.id === -1) {
    return []
  }

  // Find all PAs for this specific pitcher
  const pas = gameStore.plateAppearances.filter(pa => pa.pitcherId === selectedPitcher.value?.id)

  // Identify the opposing team's lineup to find the batter's name and lineup spot
  const isHome = selectedTeam.value?.id === gameStore.teamHome?.id
  const opposingLineup = isHome ? gameStore.visitingLineup : gameStore.homeLineup

  const options = pas.map(pa => {
    // 1. Find Batter Details
    const batterEntry = opposingLineup?.find(entry =>
      entry.player.id === pa.batterId ||
      entry.substitutions?.some(sub => sub.player.id === pa.batterId)
    )

    let batterName = 'Unknown'
    let lineupSpot = '-'

    if (batterEntry) {
      lineupSpot = String(batterEntry.battingOrder)
      if (batterEntry.player.id === pa.batterId) {
        batterName = batterEntry.player.name
      } else {
        const sub = batterEntry.substitutions?.find(s => s.player.id === pa.batterId)
        if (sub) batterName = sub.player.name
      }
    }

    // 2. Determine the PA Outcome & Extract Pitches
    let paOutcome = 'Active'
    const paTxns = gameStore.transactions.filter(t => t.plateAppearanceId === pa.id)

    const play = paTxns.find(t => t.eventType === 'PLAY') as PlayTransaction | undefined
    const pitches = paTxns.filter(t => t.eventType === 'PITCH') as PitchTransaction[]

    if (play) {
      paOutcome = `In Play (${play.outcome})`
    } else {
      if (pitches.length > 0) {
        const lastPitch = pitches[pitches.length - 1]
        const out = lastPitch?.pitchOutcome || ''
        if (['K', 'Q'].includes(out)) paOutcome = 'Strikeout'
        else if (['W', 'I', 'HP'].includes(out)) paOutcome = 'Walk/HBP'
      }
    }

    // 3. Map pitches for the Mini SVG Grid (Pitcher Perspective)
    const mappedPitches = pitches.map(p => {
      const out = p.pitchOutcome || 'B'
      let color = '#7f8c8d'
      if (['B', 'W', 'H', 'I', 'V', 'P'].includes(out)) color = '#27ae60' // Ball
      else if (out === 'X') color = '#2980b9' // In Play
      else color = '#e74c3c' // Strike

      return {
        x: 1 - (p.pitchX ?? 0.5),
        y: p.pitchY ?? 0.5,
        color
      }
    })

    return {
      id: pa.id,
      inning: pa.inning,
      lineupSpot,
      batterName,
      outcome: paOutcome,
      pitches: mappedPitches
    }
  })

  return options
})
</script>

<style scoped>
/* CSS cleaned up! Constraints removed so Quasar Layout classes handle everything */
.empty-state {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  z-index: 10;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #333;
}
</style>
